import { useEffect, useState, useCallback } from "react";
import api, { MAD } from "../lib/api";
import { StatCard, Loader } from "../components/ui-kit";
import { PeriodFilter, buildPeriodQuery } from "../components/PeriodFilter";
import { FinanceBlock } from "../components/FinanceBlock";
import { TresorerieCard } from "../components/TresorerieCard";
import {
  TrendingUp, Wallet, ShoppingBag, AlertTriangle, Receipt, Banknote, Package,
  ArrowUpRight, ArrowDownRight, Minus, Store, Building2, ChefHat, Percent, RotateCcw, Trash2, Bike, Snowflake,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, Legend,
} from "recharts";

const GOLD = "#D4AF37";
const GREEN = "#4CAF50";
const RED = "#F44336";
const PIE_COLORS = ["#D4AF37", "#C9A227", "#8C6D1F", "#E6C766", "#A8842A"];

function Trend({ ev }) {
  if (!ev || ev.pct == null) return <span className="text-xs text-[var(--tm-muted)]">—</span>;
  const up = ev.trend === "up", down = ev.trend === "down";
  const Icon = up ? ArrowUpRight : down ? ArrowDownRight : Minus;
  const c = up ? GREEN : down ? RED : "var(--tm-muted)";
  return (
    <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: c }} data-testid="trend-indicator">
      <Icon size={13} /> {ev.pct > 0 ? "+" : ""}{ev.pct}%
    </span>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div className="tm-card p-5" data-testid={testid}>
      <h3 className="font-display text-xl mb-4">{title}</h3>
      {children}
    </div>
  );
}

const tipStyle = { background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, color: "#EDE6D6" };

export default function Dashboard() {
  const [q, setQ] = useState({ period: "month" });
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((query) => {
    setLoading(true);
    api.get(`/overview?${buildPeriodQuery(query)}`).then(({ data }) => setD(data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(q); }, [load]); // eslint-disable-line

  const onPeriod = (next) => { setQ(next); load(next); };

  if (loading || !d) return (<div><PeriodFilter value={q} onChange={onPeriod} /><Loader /></div>);

  const g = d.global;
  const ev = d.comparaison.evolution;
  const dep = (key) => d.departements.find((x) => x.key === key) || null;
  const boutiques = d.departements.filter((x) => x.type === "boutique");

  const deptIcon = (t) => (t === "distribution" ? Building2 : Store);

  return (
    <div>
      <div className="mb-2">
        <p className="text-xs uppercase tracking-[0.18em] text-[var(--tm-gold)]">TIRAMESSI · Vue globale société</p>
        <h1 className="font-display text-4xl sm:text-5xl">Tableau de bord</h1>
        <p className="text-[var(--tm-muted)] text-sm mt-1.5">Activité globale — {d.periode.label} (vs {d.comparaison.label_prec}).</p>
      </div>

      <PeriodFilter value={q} onChange={onPeriod} />

      {d.finance && <TresorerieCard start={d.periode.start} end={d.periode.end} />}

      {/* Cartes globales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="tm-card p-5 tm-fade" data-testid="card-ca-global" style={{ borderColor: "rgba(212,175,55,.4)" }}>
          <div className="flex items-start justify-between">
            <p className="text-xs uppercase tracking-wider text-[var(--tm-muted)]">CA global (brut)</p>
            <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "rgba(212,175,55,.12)" }}><Banknote size={17} style={{ color: GOLD }} /></span>
          </div>
          <p className="font-display text-3xl mt-3 tabular" style={{ color: GOLD }}>{MAD(g.ca_brut)}</p>
          <div className="flex items-center justify-between mt-1"><span className="text-xs text-[var(--tm-muted)]">CA net : {MAD(g.ca_net)}</span><Trend ev={ev.ca_brut} /></div>
        </div>
        <StatCard label="Dépenses globales" value={MAD(g.depenses)} icon={Wallet} accent={RED} testid="card-depenses"
          sub={<span className="flex items-center justify-between gap-2"><span>{g.paiements_salaires_hors_depenses ? `dont salaires versés ${MAD(g.paiements_salaires_hors_depenses)}` : ""}{g.depenses_communes ? ` · commun ${MAD(g.depenses_communes)}` : ""}</span><Trend ev={ev.depenses} /></span>} />
        <div className="tm-card p-5 tm-fade" data-testid="card-benefice">
          <div className="flex items-start justify-between">
            <p className="text-xs uppercase tracking-wider text-[var(--tm-muted)]">Bénéfice estimé net</p>
            <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "rgba(212,175,55,.12)" }}><TrendingUp size={17} style={{ color: g.benefice_net >= 0 ? GREEN : RED }} /></span>
          </div>
          <p className="font-display text-3xl mt-3 tabular" style={{ color: g.benefice_net >= 0 ? GREEN : RED }}>{MAD(g.benefice_net)}</p>
          <div className="flex items-center justify-between mt-1"><span className="text-xs text-[var(--tm-muted)]">CA net {MAD(g.ca_net)} − Dép. {MAD(g.depenses)}{g.commissions_glovo ? ` − Comm. Glovo ${MAD(g.commissions_glovo)}` : ""}</span><Trend ev={ev.benefice_net} /></div>
        </div>
        <StatCard label="Marge globale" value={`${g.marge_pct}%`} icon={Percent} testid="card-marge" />
      </div>

      {/* CA par département */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {boutiques.map((b) => (
          <StatCard key={b.key} label={`CA ${b.nom.replace("TIRAMESSI ", "")}`} value={MAD(b.ca)} icon={Store}
            sub={`Dépenses ${MAD(b.depenses)}${b.depenses_communes ? ` + commun ${MAD(b.depenses_communes)}` : ""} · Bénéf. net ${MAD(b.benefice_net_apres_commun ?? b.benefice_net)}`} testid={`card-dept-${b.key}`} />
        ))}
        {d.b2b && (
          <StatCard label="CA Distribution B2B" value={MAD(d.b2b.ca_brut)} icon={Building2} accent={GOLD}
            sub={`Net : ${MAD(d.b2b.ca_net)} · Marge ${d.b2b.marge_pct}%`} testid="card-dept-b2b" />
        )}
        {(d.platforms || []).map((pl) => (
          <div key={pl.key} className="tm-card p-5 tm-fade" data-testid={`card-platform-${pl.platform_id}`} style={{ borderColor: pl.couleur + "66" }}>
            <div className="flex items-start justify-between">
              <p className="text-xs uppercase tracking-wider text-[var(--tm-muted)]">CA {pl.nom}</p>
              <span className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: pl.couleur + "22" }}><Bike size={17} style={{ color: pl.couleur }} /></span>
            </div>
            <p className="font-display text-2xl mt-3 tabular" style={{ color: pl.couleur }}>{MAD(pl.ca)}</p>
            <p className="text-xs text-[var(--tm-muted)] mt-1">{pl.nb_ventes} cmd · Net {MAD(pl.ca_net)} (promos {MAD(pl.promotions)}, comm. {MAD(pl.commission)}, frais {MAD(pl.frais_glovo)})</p>
            <p className="text-xs text-[var(--tm-muted)]" data-testid={`platform-cout-${pl.platform_id}`}>Coût matière {MAD(pl.cout_revient)}{pl.cout_estime ? ` (estimé ${pl.taux_cout_pct}%)` : ""} · Marge {pl.marge_pct}%</p>
          </div>
        ))}
        {g.depenses_communes > 0 && (
          <StatCard label="Dépenses communes" value={MAD(g.depenses_communes)} icon={Wallet} accent="#FF9800" sub="réparties au prorata du CA" testid="card-dept-commun" />
        )}
        {d.labo && (
          <StatCard label="Production (coût)" value={MAD(d.labo.cout_production)} icon={ChefHat}
            sub={`${d.labo.quantite_produite} unités · ${d.labo.nb_productions} prod.`} testid="card-dept-labo" />
        )}
      </div>

      {/* Compteurs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="Ventes" value={g.nb_ventes} icon={Receipt} testid="card-nb-ventes" />
        <StatCard label="Commandes clients" value={d.nb_commandes} icon={ShoppingBag} testid="card-nb-commandes" />
        <StatCard label="Retours B2B" value={MAD(d.retours_total)} icon={RotateCcw} accent="#FF9800" testid="card-retours" />
        <StatCard label="Pertes / Destructions" value={MAD(g.pertes)} icon={Trash2} accent={RED} testid="card-pertes" sub="indicateur — non déduit (déjà dans les achats)" />
        <StatCard label="Stock critique" value={d.stock_critique.length} icon={AlertTriangle} accent={d.stock_critique.length ? RED : GREEN} testid="card-stock-critique" />
        {d.freezer && <StatCard label="Congélateur (produits)" value={d.freezer.nb_produits} icon={Snowflake} accent="#4FC3F7" sub={`Valeur ${MAD(d.freezer.valeur)} · ${d.freezer.lots_disponibles} lots`} testid="card-freezer" />}
      </div>

      {d.finance && <div className="mb-6"><FinanceBlock finance={d.finance} /></div>}

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <ChartCard title="Évolution du chiffre d'affaires" testid="chart-evolution">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={d.serie}>
                <defs>
                  <linearGradient id="gB" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={GOLD} stopOpacity={0.5} /><stop offset="95%" stopColor={GOLD} stopOpacity={0} /></linearGradient>
                  <linearGradient id="gD" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8C6D1F" stopOpacity={0.4} /><stop offset="95%" stopColor="#8C6D1F" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#FFC244" stopOpacity={0.4} /><stop offset="95%" stopColor="#FFC244" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis dataKey="label" stroke="#8a8a8a" fontSize={12} />
                <YAxis stroke="#8a8a8a" fontSize={12} />
                <Tooltip contentStyle={tipStyle} />
                <Legend />
                <Area type="monotone" dataKey="boutique" name="Boutiques" stroke={GOLD} fill="url(#gB)" strokeWidth={2} />
                <Area type="monotone" dataKey="b2b" name="Distribution B2B" stroke="#8C6D1F" fill="url(#gD)" strokeWidth={2} />
                <Area type="monotone" dataKey="glovo" name="Glovo / Livraison" stroke="#FFC244" fill="url(#gG)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <ChartCard title="CA par département" testid="chart-pie">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={d.pie_ca} dataKey="ca" nameKey="nom" cx="50%" cy="50%" outerRadius={90} label={(x) => `${x.nom.replace("TIRAMESSI ", "")}`}>
                {d.pie_ca.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tipStyle} formatter={(v) => MAD(v)} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ChartCard title="Bénéfice net par département" testid="chart-dept-bar">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.departements.filter((x) => x.type !== "laboratoire")}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
              <XAxis dataKey="nom" stroke="#8a8a8a" fontSize={11} tickFormatter={(v) => v.replace("TIRAMESSI ", "")} />
              <YAxis stroke="#8a8a8a" fontSize={12} />
              <Tooltip contentStyle={tipStyle} formatter={(v) => MAD(v)} />
              <Legend />
              <Bar dataKey="ca" name="CA" fill={GOLD} radius={[6, 6, 0, 0]} />
              <Bar dataKey="benefice_net" name="Bénéfice net" fill={GREEN} radius={[6, 6, 0, 0]}>
                {d.departements.filter((x) => x.type !== "laboratoire").map((x, i) => <Cell key={i} fill={x.benefice_net >= 0 ? GREEN : RED} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <ChartCard title="Top produits vendus" testid="list-top-vendus">
            <div className="space-y-2">
              {d.top_vendus.length === 0 ? <p className="text-sm text-[var(--tm-muted)]">Aucune vente.</p> :
                d.top_vendus.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5" style={{ borderBottom: "1px solid var(--tm-border)" }}>
                    <span className="flex items-center gap-2"><span className="w-5 h-5 rounded-full text-[11px] flex items-center justify-center" style={{ background: "rgba(212,175,55,.15)", color: GOLD }}>{i + 1}</span>{p.nom}</span>
                    <span className="tabular font-semibold">{p.quantite}</span>
                  </div>
                ))}
            </div>
          </ChartCard>
          <ChartCard title="Produits les plus rentables" testid="list-top-rentables">
            <div className="space-y-2">
              {d.top_rentables.length === 0 ? <p className="text-sm text-[var(--tm-muted)]">—</p> :
                d.top_rentables.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5" style={{ borderBottom: "1px solid var(--tm-border)" }}>
                    <span>{p.nom}</span>
                    <span className="tabular font-semibold" style={{ color: GOLD }}>{MAD(p.marge)}</span>
                  </div>
                ))}
            </div>
          </ChartCard>
        </div>
      </div>

      {/* Snapshots du jour */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="Production du jour" testid="card-production-jour">
          <div className="flex items-end gap-4">
            <div><p className="font-display text-4xl tabular" style={{ color: GOLD }}>{d.production_jour.quantite}</p><p className="text-xs text-[var(--tm-muted)]">unités produites</p></div>
            <div className="text-sm text-[var(--tm-muted)] pb-1">{d.production_jour.nb} production(s)<br />Coût : {MAD(d.production_jour.cout)}</div>
          </div>
        </ChartCard>
        <ChartCard title="Invendus estimés du jour" testid="card-invendus-jour">
          <p className="font-display text-4xl tabular mb-2" style={{ color: d.invendus_jour.total > 0 ? "#FF9800" : GREEN }}>{d.invendus_jour.total}</p>
          {d.invendus_jour.detail.length === 0 ? <p className="text-xs text-[var(--tm-muted)]">Tout est écoulé 🎉</p> :
            <div className="space-y-1">{d.invendus_jour.detail.slice(0, 4).map((x, i) => (<div key={i} className="flex justify-between text-xs"><span>{x.nom}</span><span className="tabular text-[var(--tm-muted)]">{x.invendu} (prod {x.produit} / vendu {x.vendu})</span></div>))}</div>}
        </ChartCard>
        <ChartCard title="Stock ingrédients critique" testid="card-stock-detail">
          {d.stock_critique.length === 0 ? <p className="text-sm text-[var(--tm-muted)]">Aucun ingrédient sous le seuil.</p> :
            <div className="space-y-1.5">{d.stock_critique.slice(0, 6).map((s, i) => (
              <div key={i} className="flex justify-between text-sm"><span className="flex items-center gap-2"><AlertTriangle size={13} style={{ color: RED }} />{s.nom}</span><span className="tabular" style={{ color: RED }}>{s.quantite} {s.unite}</span></div>
            ))}</div>}
        </ChartCard>
      </div>
    </div>
  );
}
